import { createSelector } from "reselect";
import { getAccountValuesById } from "../derivedModel/accountValues";
import {
  getBalanceSheetAccountDescriptions,
  getBalanceSheetAccountHierarchy,
  getBalanceSheetAccountValues,
  getBalanceSheetDocumentExceptions,
  getBalanceSheetFinancialValues,
  getCashFlowAccountDescriptions,
  getCashFlowAccountHierarchy,
  getCashFlowAccountValues,
  getCashFlowDocumentExceptions,
  getCashFlowFinancialValues,
  getIncomeStatementAccountDescriptions,
  getIncomeStatementAccountHierarchy,
  getIncomeStatementAccountValues,
  getIncomeStatementDocumentExceptions,
  getIncomeStatementFinancialValues
} from "../derivedModel/selector";
import {
  getMappedAccountsByPage,
  getUnmappedAccountsByPage
} from "../mappedUnmappedAccounts/selector";

import {
  getConfigMetaData,
  getSpreadBalanceSheetNode,
  getSpreadBalanceSheetsPeriods,
  getSpreadCashFlowNode,
  getSpreadCashFlowsPeriods,
  getSpreadGroundPages,
  getSpreadIncomeStatementNode,
  getSpreadIncomeStatementsPeriods
} from "../spread/selector";

import {
  balanceSheetAccountPresenters,
  cashFlowAccountPresenters,
  incomeStatementAccountPresenters
} from "./accountPresenters";

import generateChartOfAccountsPresenter from "./generateChartOfAccountsPresenter";
import { pagePresenters } from "./pagePresenters";

import { SPREAD } from "../../../constants";

export const getIncomeStatementAccountPresenters = createSelector(
  getIncomeStatementAccountHierarchy,
  getIncomeStatementAccountDescriptions,
  getIncomeStatementAccountValues,
  getIncomeStatementFinancialValues,
  getIncomeStatementDocumentExceptions,
  incomeStatementAccountPresenters
);

export const getBalanceSheetAccountPresenters = createSelector(
  getBalanceSheetAccountHierarchy,
  getBalanceSheetAccountDescriptions,
  getBalanceSheetAccountValues,
  getBalanceSheetFinancialValues,
  getBalanceSheetDocumentExceptions,
  balanceSheetAccountPresenters
);

export const getCashFlowAccountPresenters = createSelector(
  getCashFlowAccountHierarchy,
  getCashFlowAccountDescriptions,
  getCashFlowAccountValues,
  getCashFlowFinancialValues,
  getCashFlowDocumentExceptions,
  cashFlowAccountPresenters
);

export const getIncomeStatementPresenter = createSelector(
  getSpreadIncomeStatementNode,
  getSpreadIncomeStatementsPeriods,
  getIncomeStatementAccountPresenters,
  generateChartOfAccountsPresenter
);

export const getCashFlowPresenter = createSelector(
  getSpreadCashFlowNode,
  getSpreadCashFlowsPeriods,
  getCashFlowAccountPresenters,
  generateChartOfAccountsPresenter
);

export const getIncomeStatementNetProfit = createSelector(
  getIncomeStatementPresenter,
  incomeStatement => incomeStatement.children[0]
);

export const getCashFlowNetProfit = createSelector(
  getCashFlowPresenter,
  cashFlow => cashFlow.children[0]
);

export const getBalanceSheetPresenter = createSelector(
  getSpreadBalanceSheetNode,
  getSpreadBalanceSheetsPeriods,
  getBalanceSheetAccountPresenters,
  generateChartOfAccountsPresenter
);

export const getTotalLiabsAndNetWorthValues = createSelector(
  getBalanceSheetAccountValues,
  getConfigMetaData,
  (valueSets, configMeta) => {
    const id = configMeta.totalLiabilitiesAndEquityFooterFieldId
      ? configMeta.totalLiabilitiesAndEquityFooterFieldId
      : SPREAD.FINANCIAL_TEMPLATE.TOTAL_LIAL_AND_NET_ID;

    const netValues = getAccountValuesById(valueSets, id);
    return netValues[0]
      ? netValues
      : getAccountValuesById(
          valueSets,
          SPREAD.FINANCIAL_TEMPLATE.TOTAL_LIAL_AND_NET_ID
        );
  }
);

export const getTotalLiabsAndNetWorthName = createSelector(
  getBalanceSheetAccountDescriptions,
  getConfigMetaData,
  (descriptions, configData) => {
    const id =
      configData.totalLiabilitiesAndEquityFooterFieldId ||
      SPREAD.FINANCIAL_TEMPLATE.TOTAL_LIABILITIES_AND_NET_WORTH_ID;
    const name = id && descriptions[id] && descriptions[id].name;

    return name || SPREAD.FINANCIAL_TEMPLATE.TOTAL_LIAL_AND_NET_DEFAULT_LABEL;
  }
);

export const getTotalAssetsValues = createSelector(
  getBalanceSheetAccountValues,
  getConfigMetaData,
  (valueSets, configData) => {
    const id = configData.totalAssetsFooterFieldId
      ? configData.totalAssetsFooterFieldId
      : SPREAD.FINANCIAL_TEMPLATE.TOTAL_ASSET_ID;

    const assetsValues = getAccountValuesById(valueSets, id);
    return assetsValues[0]
      ? assetsValues
      : getAccountValuesById(
          valueSets,
          SPREAD.FINANCIAL_TEMPLATE.TOTAL_ASSET_ID
        );
  }
);

export const getTotalAssetsName = createSelector(
  getBalanceSheetAccountDescriptions,
  getConfigMetaData,
  (descriptions, configData) => {
    const id =
      configData.totalAssetsFooterFieldId ||
      SPREAD.FINANCIAL_TEMPLATE.TOTAL_ASSETS_ID;
    const name = id && descriptions[id] && descriptions[id].name;

    return name || SPREAD.FINANCIAL_TEMPLATE.TOTAL_ASSET_DEFAULT_LABEL;
  }
);

export const getPagePresenters = createSelector(
  getSpreadGroundPages,
  getUnmappedAccountsByPage,
  getMappedAccountsByPage,
  pagePresenters
);



// WEBPACK FOOTER //
// ./src/spread/model/presenter/selector.ts